import { apiClient } from './api/client';
import type { PreparedAttachmentUpload } from '@/hooks/useAttachmentUpload';
import {
  CnpjLookupResult,
  OnboardingRecord,
  OnboardingRecordStatus,
  SaveOnboardingRecordRequest,
} from '@/types/onboarding-records';

const API_BASE = '/onboarding-records';

export const onboardingRecordService = {
  async list(status?: OnboardingRecordStatus): Promise<OnboardingRecord[]> {
    const response = await apiClient.get<OnboardingRecord[]>(API_BASE, {
      params: status ? { status } : undefined,
    })
    return response.data ?? []
  },

  async submit(id: number | string): Promise<OnboardingRecord> {
    const response = await apiClient.post<OnboardingRecord>(
      `${API_BASE}/${id}/submit`
    )
    return response.data
  },

  async complete(
    id: number | string,
    companyId: number
  ): Promise<OnboardingRecord> {
    const response = await apiClient.post<OnboardingRecord>(
      `${API_BASE}/${id}/complete`,
      { company_id: companyId }
    )
    return response.data
  },

  async cnpjLookup(cnpjs: string[]): Promise<CnpjLookupResult> {
    const limpos = cnpjs
      .map((c) => c.replace(/\D/g, ''))
      .filter((c) => c.length === 14)
    if (limpos.length === 0) return { hits: {}, truncado: false }
    const response = await apiClient.get<CnpjLookupResult>(
      `${API_BASE}/cnpj-lookup`,
      { params: { cnpjs: limpos.join(',') } }
    )
    return response.data
  },

  async get(id: number | string): Promise<OnboardingRecord> {
    const response = await apiClient.get<OnboardingRecord>(`${API_BASE}/${id}`);
    return response.data;
  },

  async create(data: SaveOnboardingRecordRequest): Promise<OnboardingRecord> {
    const response = await apiClient.post<OnboardingRecord>(API_BASE, data);
    return response.data;
  },

  async update(
    id: number | string,
    data: Partial<SaveOnboardingRecordRequest>,
  ): Promise<OnboardingRecord> {
    const response = await apiClient.put<OnboardingRecord>(
      `${API_BASE}/${id}`,
      data,
    );
    return response.data;
  },

  async remove(id: number | string): Promise<void> {
    await apiClient.delete(`${API_BASE}/${id}`);
  },

  async prepareSpreadsheetUpload(file: {
    originalName: string
    contentType: string
    size: number
  }): Promise<PreparedAttachmentUpload> {
    const response = await apiClient.post<PreparedAttachmentUpload>(
      `${API_BASE}/uploads/prepare`,
      file,
    );
    return response.data;
  },

  async getSpreadsheetUrl(id: number | string): Promise<string> {
    const response = await apiClient.get<{ url: string }>(
      `${API_BASE}/${id}/spreadsheet/url`,
    );
    return response.data.url;
  },
};
